import { HERO_BADGE_POSITIONS, ruStepIllustrations } from "./shared";
import { getScriptarioLocalizedPath } from "@/config/locales";
import type { LandingContent } from "./types";
import { intlFileHostingCopy } from "@/lib/file-hosting-sources-intl";

const locale = "pt-br" as const;

const seoFaqText = `O Scriptario transforma áudio e vídeo em texto editável com identificação de falantes, marcas de tempo, resumos e exportações. Ele ajuda em reuniões, entrevistas, aulas, podcasts, chamadas e vídeos longos que precisam virar material de trabalho. Envie um arquivo ou cole um link compatível, deixe a IA transcrever a gravação e continue com revisão, resumo, chat com IA ou download do resultado.`;

export const scriptarioPtBrContent: LandingContent = intlFileHostingCopy({
  nav: {
    pageLinks: [
      { label: "Recursos", href: getScriptarioLocalizedPath("/features", locale) },
      { label: "Preços", href: getScriptarioLocalizedPath("/pricing", locale) },
      { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) },
      { label: "Contato", href: getScriptarioLocalizedPath("/contacts", locale) },
    ],
    landingLinks: [
      { label: "Recursos", href: "#features" },
      { label: "Preços", href: "#pricing" },
      { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) },
      { label: "Contato", href: getScriptarioLocalizedPath("/contacts", locale) },
    ],
  },
  header: {
    dashboard: "Painel",
    signIn: "Entrar",
    startFree: "Começar grátis",
    openMenu: "Abrir menu",
    closeMenu: "Fechar menu",
    menu: "Menu",
  },
  hero: {
    title: <>Transcrição de áudio e vídeo<br className="hidden desktop:inline" /> para texto com <span style={{ color: "var(--brand-primary)" }}>IA</span></>,
    subtitle: <>Chamadas, podcasts, aulas, entrevistas e webinars transformados em texto pronto<br className="hidden desktop:inline" /> em minutos. Teste grátis</>,
    badges: ["99% de precisão", "Marcas de tempo", "Qualquer formato", "Chat e relatórios com IA", "Identificação de falantes", "1 hora em texto em 3 min"].map((label, i) => ({ label, ...HERO_BADGE_POSITIONS[i] })),
    trustText: "Muitos outros parceiros confiam em nós",
  },
  upload: {
    fileTab: "Arquivo",
    linkTab: "Link",
    dragOrUpload: "Arraste ou envie um arquivo",
    chooseFiles: "Escolher arquivos",
    submit: "Enviar",
    removeFile: "Remover arquivo",
    tabsLabel: "Método de envio",
    linkPlaceholder: "Cole um link do YouTube, Zoom ou Google Drive...",
    linkPlaceholderMobile: "Cole um link",
    supportedSources: "Compatível com:",
    supportedFiles: "Arquivos de áudio e vídeo em qualquer formato são aceitos",
    comingSoon: "Scriptario estará disponível em breve",
    unsupportedFile: "Somente arquivos de áudio e vídeo são aceitos",
    uploadError: "Erro ao enviar o arquivo",
    retryUpload: "Tentar novamente",
    finishingUpload: "Finalizando o envio",
  },
  cookie: {
    textLine1: "Usamos cookies necessários para o site funcionar.",
    textLine2: "Cookies opcionais são usados apenas com o seu consentimento.",
    accept: "Aceitar",
    reject: "Recusar",
  },
  how: {
    title: "Como funciona",
    subtitle: "Da gravação ao texto final em três passos simples",
    steps: [
      { step: "Passo 1", title: "Envie uma gravação", description: "Arraste um arquivo de áudio ou vídeo em qualquer formato. Ou cole um link do YouTube, Zoom ou Google Drive.", illustration: ruStepIllustrations[0], className: "" },
      { step: "Passo 2", title: "A IA transcreve em minutos", description: "A IA reconhece a fala, adiciona pontuação, separa falantes e cria marcas de tempo. Uma hora de áudio leva cerca de 3 minutos.", illustration: ruStepIllustrations[1], className: "overflow-hidden" },
      { step: "Passo 3", title: "Trabalhe com o resultado", description: "Edite o texto no navegador, peça um relatório à IA ou baixe o arquivo como PDF, Word, TXT ou legendas SRT.", illustration: ruStepIllustrations[2], className: "overflow-hidden" },
    ],
  },
  stats: {
    users: "usuários já trabalham conosco",
    hours: "horas transcritas",
    rating: "avaliação média dos usuários",
    speed: "trabalho mais rápido com gravações",
  },
  features: {
    title: "Recursos",
    subtitle: "Tudo para trabalhar com áudio e vídeo",
    cards: [
      { iconName: "feature-target", title: "99% de precisão", description: "A IA cria resumos, pontos principais e listas de tarefas a partir da transcrição quando você precisa." },
      { iconName: "feature-speedometer", title: "Processamento rápido", description: "Uma hora de gravação é transcrita em cerca de 3 minutos. Sem esperar horas." },
      { iconName: "feature-translate", title: "99 idiomas", description: "Inglês, espanhol, alemão, francês e mais 95 idiomas com detecção automática." },
    ],
    aiReports: { title: "Relatórios com IA", description: "Resumos, pontos principais e tarefas. A IA cria um relatório a partir da sua transcrição." },
    aiChat: { title: "Chat com IA", description: "Faça perguntas sobre a transcrição. A IA encontra as informações certas e responde diretamente." },
    speakers: { title: "Separação de falantes", description: "Detecte automaticamente quem está falando. Cada linha fica identificada e com marca de tempo." },
    meetingAssistant: { title: "Assistente de reuniões", description: "Grave chamadas do Google Meet, Zoom ou outras plataformas. O assistente transcreve e prepara notas automaticamente." },
    telegramBot: { title: "Versão móvel", description: "Abra o Scriptario no celular para enviar gravações, revisar transcrições e trabalhar com os resultados onde estiver." },
    linkTranscription: { title: "Transcrição por link", description: "Cole um link do YouTube, de uma gravação do Zoom ou do Google Drive e nós baixamos e transcrevemos automaticamente." },
    smallCards: [
      { iconName: "feature-text-aa", title: "Pontuação automática", description: "Pontos, vírgulas e parágrafos são adicionados automaticamente. O texto fica pronto para leitura." },
      { iconName: "feature-file", title: "Todos os formatos", description: "MP3, WAV, MP4, MOV, MKV, OGG, M4A e muitos outros." },
      { iconName: "feature-download", title: "Exportação em formatos", description: "Baixe transcrições como PDF, DOCX, TXT ou legendas SRT." },
    ],
  },
  personas: {
    title: "Para quem é",
    subtitle: "Equipes de várias áreas já economizam horas toda semana",
    items: [
      { image: "/assets/personas/manager.png", title: "Gestores e executivos", description: "Notas automáticas de reuniões e resumos de reuniões diárias. A IA destaca decisões e tarefas." },
      { image: "/assets/personas/entrepreneur.png", title: "Empreendedores", description: "Transcreva chamadas com clientes, entrevistas de descoberta e negociações. Guarde detalhes importantes em texto pesquisável." },
      { image: "/assets/personas/creator.png", title: "Criadores de conteúdo", description: "Transforme podcasts e vídeos em publicações, artigos e roteiros. Gere legendas para conteúdo sem som." },
      { image: "/assets/personas/researcher.png", title: "Pesquisadores e sociólogos", description: "Transcreva entrevistas em profundidade e grupos focais em minutos. Relatórios com IA mostram conclusões e temas importantes." },
      { image: "/assets/personas/journalist.png", title: "Jornalistas e mídia", description: "Transcreva entrevistas e coletivas em poucos minutos. Marcas de tempo e falantes ajudam a encontrar citações rapidamente." },
      { image: "/assets/personas/student.png", title: "Estudantes e professores", description: "Transforme aulas e seminários em anotações prontas. Pesquise por palavra-chave em vez de ouvir tudo de novo." },
    ],
  },
  pricing: {
    title: "Transcreva horas de gravações em minutos",
    reviewLabel: "no Trustpilot",
    usersLabel: "usuários",
    hoursLabel: "horas transcritas",
    month: "Mês",
    year: "Ano",
    yearBadge: "-50%",
    periodYearTitle: "Anual",
    periodMonthTitle: "Mensal",
    yearTotalNote: "em um único pagamento por ano",
    plans: [
      { planId: "basic", title: "Essential", description: "para tarefas regulares", ctaLabel: "Assinar" },
      { planId: "standard", title: "Professional", description: "para a maioria das tarefas", ctaLabel: "Assinar", featured: true, badge: "Popular" },
      { planId: "expert", title: "Premium", description: "para grandes volumes", ctaLabel: "Assinar" },
    ],
    tryTitle: "Quer só testar?",
    tryDescription: "15 minutos grátis por mês, para sempre",
    startFree: "Começar grátis →",
    includedTitle: "Incluído em todos os planos:",
    commonFeatures: ["Gravação de reuniões", "Relatórios de IA e chat", "Arquivos de até 5 GB", "Falantes e timestamps"],

    commonFeatureIcons: ["meetings", "ai", "files", "speakers"],
    cancel: "Cancele quando quiser",
    refund: "Política de reembolso de 7 dias",
    perMonth: "/mês",
    perYear: "/ano",
    annualBilling: "cobrado anualmente",
    monthlyBilling: "Cobrança mensal",
    overagePrefix: "acima do limite — ",
    overageSuffix: "/min",
    minutesPerMonth: (minutes) => `${minutes.toLocaleString("pt-BR")} minutos por mês`,
  },
  testimonials: {
    title: "Avaliações",
    subtitle: "O que nossos usuários dizem",
    items: [
      { quote: "Transcrevo 3 ou 4 entrevistas por semana. Antes uma chamada de 40 minutos levava mais de uma hora para virar texto. Agora recebo tudo em minutos e vejo quem disse o quê.", avatar: "/assets/testimonial-1.jpg", name: "Sarah Mitchell", role: "Editora de revista digital" },
      { quote: "Gravo aulas e envio para o serviço. Poucos minutos depois recebo a transcrição completa com parágrafos e pontuação. Posso reaproveitar o material em apostilas.", avatar: "/assets/testimonial-2.jpg", name: "Emily Carter", role: "Professora de comunicação" },
      { quote: "Uso o Scriptario para atas de reuniões e chamadas com clientes. A IA destaca tarefas e decisões automaticamente. Economiza várias horas por semana.", avatar: "/assets/testimonial-3.jpg", name: "Michael Brooks", role: "Gerente de projetos em empresa SaaS" },
    ],
  },
  faq: {
    title: "Perguntas frequentes",
    subtitle: "Respostas sobre o serviço e o fluxo de trabalho",
    items: [
      { question: "Vocês trabalham com empresas e equipes?", answer: "Sim. Ajudamos equipes de pesquisa e empresas a escolher volume e configuração de transcrição adequados. Para detalhes, escreva para hello@scriptario.com." },
      { question: "Quais idiomas o Scriptario aceita?", answer: "A transcrição está disponível em 99 idiomas, incluindo inglês, espanhol, alemão, francês e muitos outros. O idioma é detectado automaticamente." },
      { question: "Como os falantes são identificados?", answer: "O serviço detecta e identifica falantes automaticamente. Se necessário, você pode editar os nomes no editor." },
      { question: "Como vocês protegem meus arquivos e dados?", answer: "Arquivos e transcrições são criptografados e armazenados em infraestrutura protegida. Não vendemos seus dados." },
      { question: "Há limites de tamanho ou duração?", answer: "Você pode enviar arquivos de até 5 GB. A duração máxima da gravação é de 12 horas." },
      { question: "Para quais formatos posso exportar?", answer: "Transcrições finalizadas podem ser exportadas como TXT. Outros formatos fazem parte do fluxo do produto." },
      { question: "O que é o Scriptario e como funciona?", answer: seoFaqText },
    ],
  },
  cta: {
    titleLine1: "Teste o Scriptario",
    titleLine2: "grátis",
    subtitleLine1: "15 minutos gratuitos de transcrição.",
    subtitleLine2: "Sem cartão e sem compromisso",
    button: "Começar grátis",
  },
  footer: {
    tagline: "Transcrição de áudio e vídeo com IA",
    copyright: "2026 Scriptario. Todos os direitos reservados.",
    columns: [
      { title: "Produto", links: [{ label: "Recursos", href: getScriptarioLocalizedPath("/features", locale) }, { label: "Preços", href: getScriptarioLocalizedPath("/pricing", locale) }, { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) }, { label: "Contato", href: getScriptarioLocalizedPath("/contacts", locale) }] },
      { title: "Suporte", links: [{ label: "WhatsApp", href: "https://wa.me/447732727913?text=Ol%C3%A1%21%20Preciso%20de%20ajuda%20com%20o%20Scriptario." }] },
      { title: "Legal", links: [{ label: "Política de privacidade", href: getScriptarioLocalizedPath("/privacy-policy", locale) }, { label: "Termos de serviço", href: getScriptarioLocalizedPath("/terms", locale) }, { label: "Política de reembolso", href: getScriptarioLocalizedPath("/refund", locale) }, { label: "Política de cookies", href: getScriptarioLocalizedPath("/cookie-policy", locale) }] },
    ],
  },
});
